import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid2, InputAdornment, TextField } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useReferenceData } from "../../../hooks";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { ExtractionEquipmentForm as ExtractionEquipmentFormType, getExtractionEquipmentFormSchema } from "../../../types/forms/capture";
import { ExtractionEquipment } from "../../../types/model/extractionEquipment";
import FormAutocomplete from "../../General/FormAutocomplete";

interface ExtractionEquipmentFormProps {
  extractionEquipmentData?: ExtractionEquipment;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: ExtractionEquipmentFormType) => Promise<void>;
}

export default function ExtractionEquipmentForm({
  extractionEquipmentData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<ExtractionEquipmentFormProps>) {
  const { t } = useTranslation();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getExtractionEquipmentFormSchema(t);
  const methods = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      equipmentType: extractionEquipmentData?.equipmentType ?? null,
      equipmentBrandId: extractionEquipmentData?.equipmentBrandId ?? null,
      equipmentModel: extractionEquipmentData?.equipmentModel ?? null,
      equipmentPower: extractionEquipmentData?.equipmentPower ?? null,
      equipmentDepth: extractionEquipmentData?.equipmentDepth ?? null,
    },
  });

  const { data: brands = [] } = useReferenceData("construction", "brands");

  const handleFormSubmit = async (values: ExtractionEquipmentFormType) => {
    await onSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={4}>
            <TextField
              fullWidth
              label={t("captures.capture.extraction-equipment.equipment-type")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("equipmentType")}
              error={!!methods.formState.errors.equipmentType}
              helperText={methods.formState.errors.equipmentType?.message}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormAutocomplete
              name="equipmentBrandId" 
              label={t("captures.capture.extraction-equipment.equipment-brand")} 
              options={brands}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={false}
              formStyles={formStyles}
            />
          </Grid2>
          <Grid2 size={4}>
            <TextField
              fullWidth
              label={t("captures.capture.extraction-equipment.equipment-model")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("equipmentModel")}
              error={!!methods.formState.errors.equipmentModel}
              helperText={methods.formState.errors.equipmentModel?.message}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              fullWidth
              label={t("captures.capture.extraction-equipment.equipment-power")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              type="text"
              inputMode="decimal"
              {...methods.register("equipmentPower")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t("generic.units.kw")}</InputAdornment>
                  ),
                },
              }}
              error={!!methods.formState.errors.equipmentPower}
              helperText={methods.formState.errors.equipmentPower?.message}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              fullWidth
              label={t("captures.capture.extraction-equipment.equipment-depth")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              type="text"
              inputMode="decimal"
              {...methods.register("equipmentDepth")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                  ),
                },
              }}
              error={!!methods.formState.errors.equipmentDepth}
              helperText={methods.formState.errors.equipmentDepth?.message}
            />
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}